import React, { useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ScholarshipReportDb } from '../../../../../api/types/reports/accountant/scholarship-report.type';
import { StudentScholarshipsStatus } from '../../../../../api/types/students/student-scholarships/student-scholarship-entity';
import { updateScholarshipStatusOperation } from './operations/update-scholarship-status-operation';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';

interface UpdateStatusScholarshipDialogProps {
  visible: boolean;
  onHide: () => void;
  scholarship: ScholarshipReportDb;
  onStatusUpdated: () => void;
}

interface StatusOption {
  label: string;
  value: StudentScholarshipsStatus;
}

export const UpdateStatusScholarshipDialog: React.FC<
  UpdateStatusScholarshipDialogProps
> = ({ visible, onHide, scholarship, onStatusUpdated }) => {
  const [selectedStatus, setSelectedStatus] =
    useState<StudentScholarshipsStatus>(scholarship.studentScholarshipsStatus);
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const getStatusLabel = (status: StudentScholarshipsStatus): string => {
    switch (status) {
      case StudentScholarshipsStatus.PENDING:
        return 'ממתינה';
      case StudentScholarshipsStatus.APPROVED:
        return 'מאושרת';
      case StudentScholarshipsStatus.FAILED:
        return 'נכשלה';
      case StudentScholarshipsStatus.CANCELLED:
        return 'בוטלה';
      default:
        return status;
    }
  };

  const statusOptions: StatusOption[] = useMemo(
    () =>
      (
        Object.values(StudentScholarshipsStatus) as StudentScholarshipsStatus[]
      ).map((value) => ({
        label: getStatusLabel(value),
        value,
      })),
    [],
  );

  const handleApprove = async () => {
    if (!selectedStatus) return;

    try {
      await updateScholarshipStatusOperation(
        scholarship.uid,
        scholarship.scholarshipId,
        selectedStatus,
        setLoading,
      );
      onStatusUpdated();
    } catch (e) {
      console.error('Error updating scholarship status:', e);
    }
  };

  const handleOpenWarning = () => {
    if (!selectedStatus || !isStatusChanged) return;
    setShowWarning(true);
  };

  const getDangerousText = (): string => {
    if (selectedStatus === StudentScholarshipsStatus.APPROVED) {
      return (
        'אישור סטטוס המלגה כ"מאושרת" יאשר באופן סופי את כל התשלומים שבוצעו עבור מלגה זו עבור הסטודנט. ' +
        'לא ייווצרו החזרים אוטומטיים על תשלומים קיימים.'
      );
    }
    if (
      selectedStatus === StudentScholarshipsStatus.FAILED ||
      selectedStatus === StudentScholarshipsStatus.CANCELLED
    ) {
      return (
        'עדכון הסטטוס ל"נכשלה" או "בוטלה" ייצור באופן מיידי החזרים על כל התשלומים שבוצעו באמצעות מלגה זו. ' +
        'לא ניתן לבטל או להפוך את ההחזרים לאחר ביצועם.'
      );
    }
    // PENDING or any other
    return 'שינוי סטטוס המלגה עשוי להשפיע על אופן השימוש במלגה ועל תשלומים קיימים, בהתאם לסטטוס שתבחר.';
  };

  const getNotInformationText = (): string => {
    return (
      'ניתן תמיד לשנות את סטטוס המלגה בחזרה ל"ממתינה" או "מאושרת", ' +
      'אך ההחזרים שייווצרו בעקבות שינוי הסטטוס ל"נכשלה" או "בוטלה" יישארו בתוקף ולא יבוטלו אוטומטית.'
    );
  };

  const isStatusChanged =
    selectedStatus !== scholarship.studentScholarshipsStatus;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="עדכון סטטוס מלגה"
      modal
      style={{ width: '480px' }}
      footer={
        <div
          style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}
        >
          <Button label="ביטול" outlined onClick={onHide} disabled={loading} />
          <Button
            label="אישור"
            disabled={!isStatusChanged}
            onClick={handleOpenWarning}
          />
        </div>
      }
    >
      <div style={{ padding: '16px 0', direction: 'rtl' }}>
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          <p style={{ fontSize: '15px', color: '#333', margin: 0 }}>
            יש לבחור את סטטוס המלגה המבוקש.
          </p>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="scholarshipStatus"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              textAlign: 'right',
            }}
          >
            סטטוס מלגה:
          </label>
          <Dropdown
            id="scholarshipStatus"
            value={selectedStatus}
            options={statusOptions}
            onChange={(e) => setSelectedStatus(e.value)}
            placeholder="בחר סטטוס"
            style={{ width: '100%', textAlign: 'right' }}
            disabled={loading}
            optionLabel="label"
            optionValue="value"
          />
        </div>

        <div
          style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            textAlign: 'right',
            fontSize: '14px',
            color: '#374151',
          }}
        >
          <div style={{ marginBottom: '6px' }}>
            <strong>פרטי מלגה:</strong>
          </div>
          <div>מזהה מלגה: {scholarship.scholarshipId}</div>
          <div>
            סטודנט: {scholarship.firstName} {scholarship.lastName}
          </div>
          <div>תעודת זהות: {scholarship.nationalId}</div>
          <div>סכום מלגה: ₪{scholarship.scholarshipSum}</div>
          <div>סכום שנוצל: ₪{scholarship.consumeSum}</div>
        </div>
      </div>
      <WarningDialog
        header="אישור שינוי סטטוס מלגה"
        isVisible={showWarning}
        onHide={() => {
          if (!loading) {
            setShowWarning(false);
          }
        }}
        addIcon
        content={`האם אתה בטוח שברצונך לשנות את סטטוס המלגה ל"${getStatusLabel(selectedStatus)}"?`}
        dangerousContent={getDangerousText()}
        notInformation={getNotInformationText()}
        notInformationVariant="blue"
        onConfirm={async () => {
          await handleApprove();
          setShowWarning(false);
        }}
        confirmLabel="אני מבין, המשך"
        confirmLoading={loading}
      />
    </Dialog>
  );
};
